import { FilterProps, QueryFilter } from "./Filters.types";
import { Button } from "../button";
import { Icon } from "../../modules/icon";
import { DropdownButton, ButtonGroup, Dropdown } from "react-bootstrap";
import React, { useEffect } from "react";
import "./Filters.style.css";
import { useTranslation } from "react-i18next";

interface QdsSignatureField {
  fieldName: string;
  labelKey: string;
  isChecked: boolean;
  inputValue: string;
}

export function QdsSignatureDataFilter({ filter, onRemove, onFilterSet }: FilterProps) {
  const { t } = useTranslation();
  const [fields, setFields] = React.useState<QdsSignatureField[]>([
    { fieldName: "firstName", labelKey: "FILTERS.FIRST_NAME", isChecked: false, inputValue: "" },
    { fieldName: "lastName", labelKey: "FILTERS.LAST_NAME", isChecked: false, inputValue: "" },
    { fieldName: "jmbg", labelKey: "FILTERS.JMBG", isChecked: false, inputValue: "" },
    { fieldName: "pib", labelKey: "FILTERS.PIB", isChecked: false, inputValue: "" },
    { fieldName: "signingTime", labelKey: "FILTERS.SIGNING_TIME", isChecked: false, inputValue: "" },
  ]);

  useEffect(() => {
    if (!filter.defaultValues || !Array.isArray(filter.defaultValues)) return;
    const defaultData = filter.defaultValues as any[];
    const updatedFields = fields.map((field) => {
      const defaultValue = defaultData.find((d) => d.field === field.fieldName);
      // Value is already parsed by parseQueryStringFilters, so use it directly
      const fieldValue = defaultValue?.value || "";
      return {
        ...field,
        isChecked: defaultValue ? true : false,
        inputValue: fieldValue,
      };
    });
    setFields(updatedFields);
  }, [filter.defaultValues]);

  const handleCheckboxChange = (fieldName: string) => {
    setFields((prevFields) =>
      prevFields.map((field) =>
        field.fieldName === fieldName ? { ...field, isChecked: !field.isChecked } : field
      )
    );
  };

  const handleInputChange = (fieldName: string, value: string) => {
    setFields((prevFields) =>
      prevFields.map((field) =>
        field.fieldName === fieldName ? { ...field, inputValue: value } : field
      )
    );
  };

  const applyFilter = () => {
    // Filter only checked fields with non-empty input values
    const activeFields = fields.filter((field) => field.isChecked && field.inputValue.trim());

    if (activeFields.length === 0) {
      // If no fields are selected, emit empty array to clear filters
      onFilterSet([], filter);
      return;
    }

    // Create separate QueryFilter objects for each selected field
    const queryFilters: QueryFilter[] = activeFields.map((field) => {
      // For signingTime, send only the date (yyyy-MM-dd), for others send the value as-is
      const fieldValue = field.fieldName === "signingTime" ? field.inputValue : field.inputValue;
      return {
        name: "signaturesData",
        type: "qdsSignatureData",
        field: field.fieldName,
        value: JSON.stringify(fieldValue),
      } as any;
    });

    // Emit all filters at once as an array
    onFilterSet(queryFilters, filter);
  };

  const renderAppliedNames = (): string => {
    const activeFields = fields.filter((f) => f.isChecked && f.inputValue.trim());
    if (activeFields.length === 0) return "";
    return activeFields.map((f) => `${t(f.labelKey)}: ${f.inputValue}`).join(", ");
  };

  return (
    <div className="mr-3 my-1">
      <div className="btn-group">
        <DropdownButton
          as={ButtonGroup}
          variant="outline-dark"
          onToggle={(show) => {
            // Reset on close
            if (!show) {
              // Optional: reset to applied values
            }
          }}
          onSelect={() => true}
          title={
            <>
              {t(filter.name)}: {!!fields.some((f) => f.isChecked) && renderAppliedNames()}
              &nbsp;
            </>
          }
        >
          <div>
            <div className="px-2 pt-2">
              {fields.map((field, key) => (
                <div key={key} className="mb-3">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`qds-${field.fieldName}`}
                      name={field.fieldName}
                      checked={field.isChecked}
                      onChange={() => handleCheckboxChange(field.fieldName)}
                    />
                    <label htmlFor={`qds-${field.fieldName}`} className="form-check-label">
                      {t(field.labelKey)}
                    </label>
                  </div>
                  {field.isChecked &&
                    (field.fieldName === "signingTime" ? (
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={field.inputValue}
                        onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                        placeholder={t(field.labelKey)}
                      />
                    ) : (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={field.inputValue}
                        onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                        placeholder={t(field.labelKey)}
                      />
                    ))}
                </div>
              ))}
              <div className="apply" style={{ position: "sticky", bottom: 0 }}>
                <Dropdown.Item className="btn btn-primary mt-3" onClick={applyFilter}>
                  {t("APPLY")}
                </Dropdown.Item>
              </div>
            </div>
          </div>
        </DropdownButton>
        <Button className="btn btn-outline-dark" onClick={() => onRemove(filter)}>
          <Icon title="Remove" type="fa-solid fa-xmark" size={16} />
        </Button>
      </div>
    </div>
  );
}
